/**
 * Drop-review filter — E3: this drop is a review filter, not a second mode.
 * File ids for a batch live in sessionStorage; the URL keeps `?review=drop&batch=…`.
 */
import { BB_LETTER_TO_SECTION, sectionForEntry, type BbLetter } from '@/lib/bbSectionMap';
import type { AttentionRow, ProvenanceMap } from '@/components/brand-bible/NeedsAttentionQueue';
import type { BbFileReadResponse } from '@/services/bbFilesService';
import type { DropFileRow } from '../FileDropZone';

/** E7: 0 drafts is not Done. Blame-free + what file types actually help. */
export const ZERO_YIELD_HELP =
  'Need a doc with business facts or real client copy (.docx .pdf .txt .md)';

/** Map a successful /read payload onto the drop row. */
export function rowFromRead(res: BbFileReadResponse): Partial<DropFileRow> {
  if (res.status === 'failed') {
    return { state: 'error', detail: res.reason ?? 'Read failed' };
  }
  const n = res.entries_drafted;
  if (n <= 0) {
    return { state: 'empty', detail: ZERO_YIELD_HELP };
  }
  const parts = [`${n} draft${n === 1 ? '' : 's'}`];
  const sectionBits = Object.entries(res.sections ?? {})
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([section, count]) => `${section} ${count}`);
  if (sectionBits.length) parts.push(sectionBits.join(' · '));
  if (res.skipped_existing > 0) parts.push(`${res.skipped_existing} already on file`);
  if (res.dropped_invalid > 0) parts.push(`${res.dropped_invalid} dropped`);
  return { state: 'done', detail: parts.join(' · ') };
}

export const DROP_REVIEW_VALUE = 'drop';
export const DROP_REVIEW_PARAM = 'review';
export const DROP_BATCH_PARAM = 'batch';

const storageKey = (batchId: string) => `bb-drop-batch:${batchId}`;

export function newDropBatchId(): string {
  return crypto.randomUUID();
}

export function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

export function persistDropBatch(batchId: string, fileIds: string[]): void {
  try {
    sessionStorage.setItem(storageKey(batchId), JSON.stringify({ fileIds: uniqueIds(fileIds) }));
  } catch {
    // Private mode / quota — in-memory session on the page still works.
  }
}

export function loadDropBatch(batchId: string): string[] {
  try {
    const raw = sessionStorage.getItem(storageKey(batchId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { fileIds?: unknown };
    return Array.isArray(parsed.fileIds)
      ? parsed.fileIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      : [];
  } catch {
    return [];
  }
}

export function rowKey(table: string, entryId: string): string {
  return `${table}:${entryId}`;
}

/** Unverified rows whose extraction provenance belongs to this drop's files. */
export function filterRowsByFileIds(
  rows: AttentionRow[],
  provenance: ProvenanceMap | undefined,
  fileIds: string[],
): AttentionRow[] {
  if (!provenance || fileIds.length === 0) return [];
  const want = new Set(fileIds);
  return rows.filter((row) => {
    const p = provenance.get(rowKey(row.table, row.entry.id));
    return Boolean(p?.file_id && want.has(p.file_id));
  });
}

export function countsByLetter(rows: AttentionRow[]): { letter: BbLetter; count: number }[] {
  const counts = {} as Partial<Record<BbLetter, number>>;
  for (const row of rows) {
    const section = sectionForEntry(row.table, row.entry);
    if (!section) continue;
    const letter = (Object.keys(BB_LETTER_TO_SECTION) as BbLetter[]).find(
      (l) => BB_LETTER_TO_SECTION[l] === section,
    );
    if (letter) counts[letter] = (counts[letter] ?? 0) + 1;
  }
  return (Object.keys(counts) as BbLetter[])
    .sort()
    .map((letter) => ({ letter, count: counts[letter] ?? 0 }));
}

export function isVoiceReviewRow(row: AttentionRow | null): boolean {
  if (!row) return false;
  if (row.table === 'voice_profile') return true;
  if (row.table !== 'chunks') return false;
  const section = (row.entry as { section?: string }).section;
  return section === 'B';
}
